import { z } from "zod";

export const OFFER_INTERESTS = [
  "devops_contract",
  "audit",
  "rescue",
  "consult",
  "local_website",
  "other",
  "scan",
] as const;

export type OfferInterest = (typeof OFFER_INTERESTS)[number];

export const OFFER_LABELS: Record<OfferInterest, string> = {
  devops_contract: "DevOps contract",
  audit: "DevOps and cloud cost audit",
  rescue: "Rescue for an AI-built app",
  consult: "60-minute stack consult",
  local_website: "Local website",
  other: "Other",
  scan: "Security scan",
};

const optionalText = z.string().trim().max(200).optional().or(z.literal(""));

const optionalUrl = z
  .string()
  .trim()
  .max(300)
  .optional()
  .or(z.literal(""))
  .refine(
    (value) => {
      if (!value) return true;
      try {
        const url = new URL(value);
        return url.protocol === "https:" || url.protocol === "http:";
      } catch {
        return false;
      }
    },
    { message: "Enter a full link starting with https://, or leave it blank." },
  );

/** Offers that ask for a repo link (code review, audit, rescue, scan). */
export const REPO_OFFERS: readonly OfferInterest[] = ["rescue", "audit", "scan"];
/** Offers that ask for a service area and current website. */
export const LOCAL_OFFERS: readonly OfferInterest[] = ["local_website"];

/**
 * Offer-form submissions that open a HubSpot deal.
 * Scan, consult, DevOps, and other stay contacts only.
 */
export const HIGH_INTENT_OFFERS: readonly OfferInterest[] = [
  "audit",
  "rescue",
  "local_website",
];

export function isHighIntentOffer(offer: OfferInterest): boolean {
  return HIGH_INTENT_OFFERS.includes(offer);
}

export const offerFormFieldsSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(120),
  email: z.string().trim().email("Enter a valid email address").max(200),
  company: z.string().trim().max(160).optional().or(z.literal("")),
  phone: z
    .string()
    .trim()
    .max(40)
    .optional()
    .or(z.literal(""))
    .refine(
      (value) => {
        const phone = value ?? "";
        if (!phone) return true;
        const digits = phone.replace(/\D/g, "");
        return digits.length >= 7 && digits.length <= 15 && /^[0-9+().\-\s]+$/.test(phone);
      },
      { message: "Enter a phone number we can call, or leave it blank." },
    ),
  offer: z.enum(OFFER_INTERESTS),
  message: z
    .string()
    .trim()
    .min(10, "Add a short note so I know how to help.")
    .max(5000),
  consentAccepted: z.boolean().refine((value) => value === true, {
    message: "Please agree to the Privacy Statement.",
  }),
  website: z.string().max(200).optional().or(z.literal("")),
  repoUrl: optionalUrl,
  serviceArea: z.string().trim().max(160).optional().or(z.literal("")),
  currentWebsite: optionalUrl,
  utmSource: optionalText,
  utmMedium: optionalText,
  utmCampaign: optionalText,
  utmTerm: optionalText,
  utmContent: optionalText,
  referrer: optionalText,
  page: optionalText,
});

export const offerContactRequestSchema = z.object({
  formType: z.literal("offer"),
  turnstileToken: z.string().optional(),
  formData: offerFormFieldsSchema,
});

export type OfferFormFields = z.infer<typeof offerFormFieldsSchema>;

/**
 * Map ?offer= values from CTAs onto the form's stored interest.
 * Unknown values fall through to Other so the form still loads.
 */
export function offerFromQuery(value: string | null | undefined): OfferInterest {
  const normalized = (value ?? "").trim().toLowerCase();
  if (
    normalized === "devops" ||
    normalized === "devops_contract" ||
    normalized === "hire" ||
    normalized === "contract"
  ) {
    return "devops_contract";
  }
  if (
    normalized === "local" ||
    normalized === "local_website" ||
    normalized === "website"
  ) {
    return "local_website";
  }
  if (normalized === "audit" || normalized === "cloud-audit" || normalized === "cost-audit") {
    return "audit";
  }
  if (normalized === "rescue" || normalized === "app-rescue" || normalized === "vibe") {
    return "rescue";
  }
  if (normalized === "consult" || normalized === "stack" || normalized === "stack-consult") {
    return "consult";
  }
  if (
    normalized === "scan" ||
    normalized === "security-scan" ||
    normalized === "security_scan"
  ) {
    return "scan";
  }
  return "other";
}

export function splitPersonName(name: string): { firstName: string; lastName?: string } {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: "Unknown" };
  if (parts.length === 1) return { firstName: parts[0] };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
}
